import { Grid } from '@mui/material';
import React, { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useOrderFormContext } from '../context/OrderFormContext';
import { formatDate } from '../../../app/utils';
import BoxWhiteContainer from '../../../components/Containers/BoxWhiteContainer';
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem';
import { LabelItem } from '../../../components/Typographies/LabelItem';
import messages from './messages';

interface ConsignmentInfoProps {
    consignmentIndex: number;
}

const ConsignmentInfo: React.FC<ConsignmentInfoProps> = ({ consignmentIndex }) => {
    const { formatMessage } = useIntl();
    const { control } = useFormContext();
    const { variant } = useOrderFormContext();

    const currencyObj = useWatch({ control, name: `bookings.${consignmentIndex}.currency` });
    const consignmentReferenceNumber = useWatch({ control, name: `bookings.${consignmentIndex}.referenceNumber` });
    const consignmentTrackingNumber = useWatch({ control, name: `bookings.${consignmentIndex}.trackingNumber` });
    const description = useWatch({ control, name: `bookings.${consignmentIndex}.description` });
    const shipFrom = useWatch({ control, name: `bookings.${consignmentIndex}.shipFrom.locationId` });
    const deliverTo = useWatch({ control, name: `bookings.${consignmentIndex}.deliverTo.locationId` });
    const loadingDate = formatDate(useWatch({ control, name: `bookings.${consignmentIndex}.shipFrom.loadingDate` }));
    const deliveryDate = formatDate(useWatch({ control, name: `bookings.${consignmentIndex}.deliverTo.deliveryDate` }));

    const shipperObj = useWatch({ control, name: `bookings.${consignmentIndex}.shipper` });
    const loadTypeObj = useWatch({ control, name: `bookings.${consignmentIndex}.loadType` });
    const shipFromLocation = useWatch({ control, name: `bookings.${consignmentIndex}.shipFrom.location` });
    const deliverToLocation = useWatch({ control, name: `bookings.${consignmentIndex}.deliverTo.location` });

    const getCurrencyLabel = useCallback(() => {
        if (!currencyObj) return 'N/A';
        return `${currencyObj.iso3} - ${currencyObj.localisedSymbol}`;
    }, [currencyObj]);

    const getShipperName = () => {
        return shipperObj?.name ?? 'N/A';
    };

    const getLocationName = (locationId: number, type: 'shipFrom' | 'deliverTo') => {
        if (type === 'shipFrom') return shipFromLocation?.name ?? 'N/A';
        return deliverToLocation?.name ?? 'N/A';
    };

    const getLoadTypeLabel = () => {
        return loadTypeObj?.name ?? 'N/A';
    };

    return (
        <>
            <BoxWhiteContainer >
                <Grid container>
                    <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', mr: '-10px' }}>
                        <LabelItem>{formatMessage(messages.consignmentInformation)}</LabelItem>
                    </Grid>
                    {variant === 'summary' ? (
                        <>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.shipper)}
                                    value={getShipperName()}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', pl: '12px' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.currency)}
                                    value={getCurrencyLabel() ?? 'N/A'}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', pl: '20px' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.customerReferenceNumber)}
                                    value={consignmentReferenceNumber ?? 'N/A'}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                            <Grid item xs={3} sx={{ mr: '-10px' }}></Grid>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.loadType)}
                                    value={getLoadTypeLabel()}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-start', pl: '12px' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.consignmentTrackingNumber)}
                                    value={consignmentTrackingNumber ?? 'N/A'}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                            <Grid item xs={3} sx={{ mr: '-10px' }}></Grid>
                            <Grid item xs={12} md={9} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.description)}
                                    value={description ?? 'N/A'}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.shipper)}
                                    value={getShipperName()}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', pl: '12px' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.currency)}
                                    value={getCurrencyLabel() ?? 'N/A'}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', pl: '20px' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.customerReferenceNumber)}
                                    value={consignmentReferenceNumber ?? 'N/A'}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                            <Grid item xs={3} sx={{ mr: '-10px' }}></Grid>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.loadType)}
                                    value={getLoadTypeLabel()}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-start', pl: '12px' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.consignmentTrackingNumber)}
                                    value={consignmentTrackingNumber ?? 'N/A'}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                            <Grid item xs={3} sx={{ mr: '-10px' }}></Grid>
                            <Grid item xs={12} md={9} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.description)}
                                    value={description ?? 'N/A'}
                                    isSmallScreen={false}
                                    allowOverlap={true}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </BoxWhiteContainer>
            {variant !== 'summary' && (
                <>
                    <BoxWhiteContainer >
                        <Grid container>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', mr: '-10px' }}>
                                <LabelItem>{formatMessage(messages.shipFromDeliverTo)}</LabelItem>
                            </Grid>
                            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.shipFrom)}
                                    value={getLocationName(shipFrom, 'shipFrom') ?? 'N/A'}
                                    isSmallScreen={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.deliverTo)}
                                    value={getLocationName(deliverTo, 'deliverTo') ?? 'N/A'}
                                    isSmallScreen={false}
                                />
                            </Grid>
                            <Grid item md={3} sx={{ mr: '-10px' }}></Grid>
                            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.requestedPickupDate)}
                                    value={loadingDate}
                                    isSmallScreen={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.requestedDeliveryDate)}
                                    value={deliveryDate}
                                    isSmallScreen={false}
                                />
                            </Grid>
                        </Grid>
                    </BoxWhiteContainer>
                </>
            )}

        </>
    );
};

export default ConsignmentInfo;
